import { useAppData } from '../context/AppDataContext';
import { userCanAccessGymModule } from '../utils/gymModules';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, Stack, Alert, CircularProgress, IconButton, Snackbar, TextField, InputAdornment, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { MonitorWeight, ContentCopy, Refresh, Visibility, VisibilityOff, WhatsApp } from '@mui/icons-material';
import api, { SERVER_URL, gymsAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';

export default function Integrations() {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const { gyms } = useAppData();
  const canCommunicate = userCanAccessGymModule(currentUser, gyms, 'communications');
  const gymId = currentUser?.gymId?._id || currentUser?.gymId;

  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // WhatsApp Business Cloud API Gym settings
  const [whatsappGateway, setWhatsappGateway] = useState('platform');
  const [whatsappPhoneId, setWhatsappPhoneId] = useState('');
  const [whatsappToken, setWhatsappToken] = useState('');
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadKey();
    if (gymId && canCommunicate) {
      loadGymSettings();
    }
  }, [gymId, canCommunicate]);

  const loadGymSettings = async () => {
    try {
      const res = await gymsAPI.getOne(gymId);
      if (res.data?.data) {
        setWhatsappGateway(res.data.data.whatsappGateway || 'platform');
        setWhatsappPhoneId(res.data.data.whatsappPhoneNumberId || '');
        setWhatsappToken(res.data.data.whatsappAccessToken || '');
      }
    } catch (e) {
      console.error('Failed to load gym WhatsApp settings:', e);
    }
  };

  const saveWhatsAppSettings = async () => {
    if (!gymId) return;
    try {
      setWhatsappSaving(true);
      setError('');
      await gymsAPI.update(gymId, {
        whatsappGateway,
        whatsappPhoneNumberId: whatsappPhoneId.trim(),
        whatsappAccessToken: whatsappToken.trim(),
      });
      setSuccessMsg('WhatsApp Business settings updated successfully!');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save WhatsApp settings.');
    } finally {
      setWhatsappSaving(false);
    }
  };

  const loadKey = async () => {
    try {
      setLoading(true);
      const res = await api.get('/integrations/key');
      setApiKey(res.data.key);
    } catch (e) {
      setError('Failed to load integration key.');
    } finally {
      setLoading(false);
    }
  };

  const regenerateKey = async () => {
    setConfirmOpen(true);
  };

  const doRegenerate = async () => {
    setConfirmOpen(false);
    try {
      setLoading(true);
      const res = await api.post('/integrations/key/regenerate');
      setApiKey(res.data.key);
    } catch (e) {
      setError('Failed to regenerate key.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;

  // Webhook base URL: explicit env var takes priority, then fall back to
  // stripping /api from REACT_APP_API_URL (already computed as SERVER_URL).
  const webhookBaseUrl = process.env.REACT_APP_WEBHOOK_BASE_URL || SERVER_URL;

  return (
    <Box>

      {/* Header */}
      <PageHeader
        title="Integrations"
        subtitle="Connect Smart Scales and configure WhatsApp Business API for your gym."
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadKey}>Refresh</Button>}
      />


      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, borderColor: 'primary.main', bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={1}>Your API Secret Key</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This key is required to authenticate requests from your devices. Do not share this key publicly. Add it to your device's webhook headers as <kbd>x-api-key</kbd>.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              fullWidth
              size="small"
              type={showKey ? 'text' : 'password'}
              value={apiKey || 'Not generated yet'}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowKey(!showKey)} edge="end" size="small">
                      {showKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { bgcolor: 'white' }
              }}
            />
            <Button variant="contained" startIcon={<ContentCopy />} onClick={() => handleCopy(apiKey)}>Copy</Button>
            <Button variant="outlined" color="error" startIcon={<Refresh />} onClick={regenerateKey}>Regenerate</Button>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" fontWeight={700} mb={2}>Available Integrations</Typography>
      <Stack spacing={3}>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Box sx={{ p: 1.5, bgcolor: '#0284C718', borderRadius: 2, color: '#0284C7' }}>
                <MonitorWeight />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>InBody Smart Scale</Typography>
                <Typography variant="body2" color="text.secondary">Sync weight, fat percentage, and muscle mass directly into member profiles.</Typography>
              </Box>
            </Stack>
            <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">Webhook URL</Typography>
            <Stack direction="row" spacing={1} mt={0.5}>
              <TextField 
                fullWidth 
                size="small" 
                value={`${webhookBaseUrl}/api/integrations/inbody/webhook`} 
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }} 
              />
              <Button variant="outlined" size="small" onClick={() => handleCopy(`${webhookBaseUrl}/api/integrations/inbody/webhook`)}>Copy</Button>
            </Stack>
          </CardContent>
        </Card>

        {/* WhatsApp Business API Card */}
        {canCommunicate && <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Box sx={{ p: 1.5, bgcolor: '#25D36618', borderRadius: 2, color: '#25D366' }}>
                <WhatsApp />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>Branded WhatsApp Business API</Typography>
                <Typography variant="body2" color="text.secondary">Configure your gym-specific WhatsApp Business Cloud API to send automated messages directly under your brand name.</Typography>
              </Box>
            </Stack>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>WhatsApp Gateway Account</FormLabel>
              <RadioGroup
                row
                value={whatsappGateway}
                onChange={(e) => setWhatsappGateway(e.target.value)}
              >
                <FormControlLabel value="platform" control={<Radio size="small" />} label="Fitistic Platform Default (Shared)" />
                <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom Branded WhatsApp Business Account" />
              </RadioGroup>
            </FormControl>

            {whatsappGateway === 'platform' ? (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 3, maxWidth: 500 }}>
                <Typography variant="body2" color="text.secondary">
                  Sending notifications via the shared <strong>Fitistic default gateway</strong>. No additional setup is required.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ maxWidth: 500, mb: 3 }}>
                <TextField 
                  label="WhatsApp Phone Number ID" 
                  size="small" 
                  placeholder="e.g. 109284712019483"
                  value={whatsappPhoneId}
                  onChange={(e) => setWhatsappPhoneId(e.target.value)}
                  fullWidth
                />
                <TextField 
                  label="WhatsApp Access Token" 
                  size="small" 
                  placeholder="e.g. EAAG..."
                  type="password"
                  value={whatsappToken}
                  onChange={(e) => setWhatsappToken(e.target.value)}
                  fullWidth
                />
              </Stack>
            )}

            <Box>
              <Button 
                variant="contained" 
                size="small" 
                onClick={saveWhatsAppSettings} 
                disabled={whatsappSaving}
              >
                {whatsappSaving ? 'Saving...' : 'Save WhatsApp Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>}
      </Stack>

      <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} message="Copied to clipboard!" />
      <Snackbar open={!!successMsg} autoHideDuration={2000} onClose={() => setSuccessMsg('')} message={successMsg} />

      <ConfirmDialog
        open={confirmOpen}
        title="Regenerate Integration Key?"
        content="Existing hardware devices will stop syncing until updated with the new API secret key."
        onConfirm={doRegenerate}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
